import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, PlusCircle, Trash2, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import axios from 'axios';
import { useAuthContext } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

// Helper functions for billing frequency normalization
const normalizeBillingFrequency = (value: string): string => {
  const frequencyMap: Record<string, string> = {
    'month': 'Mensal',
    'year': 'Anual',
    'one-time': 'Pagamento Único'
  };
  
  return frequencyMap[value] || value;
};

const reverseNormalizeBillingFrequency = (value: string): string => {
  const frequencyMap: Record<string, string> = {
    'monthly': 'month',
    'annually': 'year',
    'one-time': 'one-time'
  };
  
  return frequencyMap[value] || value;
};

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface PriceRecord {
  id: string;
  currency: string;
  price_amount: string;
  cost_amount: string;
  margin: string;
}

interface Vendor {
  id: string;
  name: string;
}

export default function NewSubscriptionPlan() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const [activeTab, setActiveTab] = useState("plano");
  const [loading, setLoading] = useState(true);
  
  // Informações básicas do plano
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [planFamilies, setPlanFamilies] = useState<SubscriptionPlanFamily[]>([]);
  const [selectedFamily, setSelectedFamily] = useState('');
  const [serviceType, setServiceType] = useState('licensing');
  
  // Informações de preço
  const [priceRecords, setPriceRecords] = useState<PriceRecord[]>([]);
  const [currency, setCurrency] = useState('BRL');
  const [priceAmount, setPriceAmount] = useState('');
  const [costAmount, setCostAmount] = useState('');
  const [margin, setMargin] = useState('');
  const [pricingModel, setPricingModel] = useState('flat');
  const [unitMeasure, setUnitMeasure] = useState('');
  const [minimumQuantity, setMinimumQuantity] = useState('1');
  const [maximumQuantity, setMaximumQuantity] = useState('100');
  const [calculationType, setCalculationType] = useState<'markup' | 'margin'>('margin');
  
  // Informações de cobrança
  const [billingFrequency, setBillingFrequency] = useState('month');
  const [billingModel, setBillingModel] = useState('on creation');
  const [billingPeriodDuration, setBillingPeriodDuration] = useState('1');
  const [subscriptionPeriodType, setSubscriptionPeriodType] = useState('month');
  const [subscriptionPeriodDuration, setSubscriptionPeriodDuration] = useState('1');
  const [billingCycles, setBillingCycles] = useState('forever');
  
  // Informações de fornecedor
  const [selectedVendor, setSelectedVendor] = useState('0198c515-1afa-7c27-8431-ef18d611d2b5');
  const [vendorSubscriptionName, setVendorSubscriptionName] = useState('');
  const [vendorPartNumber, setVendorPartNumber] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch plan families
        const { data: familiesData, error: familiesError } = await supabase
          .from('subscription_plan_families')
          .select('*')
          .eq('is_active', true)
          .order('name');

        if (familiesError) throw familiesError;
        setPlanFamilies(familiesData || []);

        if (familiesData && familiesData.length > 0) {
          setSelectedFamily(familiesData[0].id);
        }
        
        // Tentar buscar fornecedores se a tabela existir
        try {
          const { data: vendorsData } = await supabase
            .from('vendors')
            .select('*')
            .order('name');
            
          setVendors(vendorsData || []);
        } catch (vendorError) {
          console.log('Tabela de fornecedores não encontrada ou não acessível');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast({
          variant: 'destructive',
          title: 'Erro ao carregar dados',
          description: 'Não foi possível carregar os dados necessários. Por favor, recarregue a página.',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Monitorar mudanças no modelo de precificação para definir automaticamente a unidade de medida
  useEffect(() => {
    if (pricingModel === 'per unit' && !unitMeasure) {
      setUnitMeasure('user'); // Define 'Por usuário' como padrão
    }
  }, [pricingModel, unitMeasure]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setSku('');
    setPriceRecords([]);
    setCurrency('BRL');
    setPriceAmount('');
    setCostAmount('');
    setMargin('');
    setPricingModel('flat');
    setUnitMeasure('');
    setMinimumQuantity('1');
    setMaximumQuantity('100');
    setBillingFrequency('month');
    setBillingModel('on creation');
    setBillingPeriodDuration('1');
    setSubscriptionPeriodType('month');
    setSubscriptionPeriodDuration('1');
    setBillingCycles('forever');
    setSelectedVendor('none');
    setVendorSubscriptionName('');
    setVendorPartNumber('');
    setServiceType('licensing');
    setActiveTab('plano');
  };

  const addPriceRecord = () => {
    // Validar se pelo menos o preço foi preenchido
    if (!priceAmount || parseFloat(priceAmount.replace(',', '.')) <= 0) {
      toast({
        variant: 'destructive',
        title: 'Erro ao adicionar preço',
        description: 'Informe um preço válido para adicionar à lista.',
      });
      return;
    }

    setPriceRecords([...priceRecords, {
      id: Date.now().toString(),
      currency: currency,
      price_amount: priceAmount,
      cost_amount: costAmount,
      margin: margin
    }]);

    // Reset the form fields
    setCurrency('BRL');
    setPriceAmount('');
    setCostAmount('');
    setMargin('');
  };

  const removePriceRecord = (id: string) => {
    setPriceRecords(priceRecords.filter(record => record.id !== id));
  };

  const updatePriceRecord = (id: string, field: keyof PriceRecord, value: string) => {
    setPriceRecords(priceRecords.map(record => {
      if (record.id !== id) return record;

      // Permite entrada de vírgula para campos numéricos
      const formattedValue = field === 'price_amount' || field === 'cost_amount' || field === 'margin'
        ? value.replace('.', ',')
        : value;
      
      const updatedRecord = { ...record, [field]: formattedValue };
      const cost = parseDecimal(updatedRecord.cost_amount) || 0;

      // Se o campo atualizado for preço, calcular a margem
      if (field === 'price_amount' && cost > 0) {
        const price = parseDecimal(formattedValue) || 0;
        if (price > 0) {
          let margin: number;
          if (calculationType === 'markup') {
            // Mark-up = ((Preço - Custo) / Custo) * 100
            margin = ((price - cost) / cost) * 100;
          } else {
            // Margem = ((Preço - Custo) / Preço) * 100
            margin = ((price - cost) / price) * 100;
          }
          updatedRecord.margin = formatDecimal(margin.toFixed(2));
        }
      }
      
      // Se o campo atualizado for margem, calcular o preço
      if (field === 'margin' && cost > 0) {
        const margin = parseDecimal(formattedValue) || 0;
        if (margin >= 0) {
          let price: number;
          if (calculationType === 'markup') {
            // Preço = Custo * (1 + Markup/100)
            price = cost * (1 + margin / 100);
          } else {
            // Preço = Custo / (1 - Margem/100)
            if (margin >= 100) return updatedRecord; // Evitar divisão por zero
            price = cost / (1 - margin / 100);
          }
          updatedRecord.price_amount = formatDecimal(price.toFixed(2));
        }
      }

      // Se o campo atualizado for custo, recalcular baseado no que já está preenchido
      if (field === 'cost_amount' && parseDecimal(formattedValue) > 0) {
        const cost = parseDecimal(formattedValue);
        
        // Se tiver margem, recalcular preço
        if (updatedRecord.margin) {
          const margin = parseDecimal(updatedRecord.margin);
          let price: number;
          if (calculationType === 'markup') {
            // Preço = Custo * (1 + Markup/100)
            price = cost * (1 + margin / 100);
          } else {
            // Preço = Custo / (1 - Margem/100)
            if (margin >= 100) return updatedRecord; // Evitar divisão por zero
            price = cost / (1 - margin / 100);
          }
          updatedRecord.price_amount = formatDecimal(price.toFixed(2));
        }
        // Se tiver preço, recalcular margem
        else if (updatedRecord.price_amount) {
          const price = parseDecimal(updatedRecord.price_amount);
          let margin: number;
          if (calculationType === 'markup') {
            // Mark-up = ((Preço - Custo) / Custo) * 100
            margin = ((price - cost) / cost) * 100;
          } else {
            // Margem = ((Preço - Custo) / Preço) * 100
            margin = ((price - cost) / price) * 100;
          }
          updatedRecord.margin = formatDecimal(margin.toFixed(2));
        }
      }

      return updatedRecord;
    }));
  };

  // Funções para formatar e converter valores decimais
  const formatDecimal = (value: number | string): string => {
    if (!value && value !== 0) return '';
    return typeof value === 'number' 
      ? value.toString().replace('.', ',')
      : value.toString().replace('.', ',');
  };

  const parseDecimal = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace(',', '.'));
  };

  const updatePriceFromCostAndMargin = (cost: string, marginValue: string) => {
    try {
      const costNum = parseDecimal(cost);
      const marginNum = parseDecimal(marginValue);
      
      console.log(`Entrada para cálculo de preço: Custo=${costNum}, ${calculationType === 'markup' ? 'Mark-up' : 'Margem'}=${marginNum}%, Método=${calculationType}`);
      
      if (!isNaN(costNum) && !isNaN(marginNum) && costNum > 0) {
        let calculatedPrice: number;

        if (calculationType === 'markup') {
          // Preço por mark-up = Custo * (1 + (Mark-up/100))
          calculatedPrice = costNum * (1 + (marginNum / 100));
          console.log(`Cálculo de preço por mark-up: ${costNum} * (1 + (${marginNum} / 100)) = ${calculatedPrice}`);
        } else {
          // Preço por margem = Custo / (1 - (Margem/100))
          // A margem não pode ser 100% neste cálculo, pois resultaria em divisão por zero
          if (marginNum >= 100) {
            console.log('Margem deve ser menor que 100% para cálculo por margem');
            return '';
          }
          calculatedPrice = costNum / (1 - (marginNum / 100));
          console.log(`Cálculo de preço por margem: ${costNum} / (1 - (${marginNum} / 100)) = ${calculatedPrice}`);
        }
        
        setPriceAmount(formatDecimal(calculatedPrice.toFixed(2)));
        return formatDecimal(calculatedPrice.toFixed(2));
      } else {
        console.log('Valores inválidos para cálculo de preço');
        return '';
      }
    } catch (error) {
      console.error('Erro ao calcular preço:', error);
      return '';
    }
  };

  const updateMarginFromPriceAndCost = (price: string, cost: string) => {
    try {
      const priceNum = parseDecimal(price);
      const costNum = parseDecimal(cost);
      
      console.log(`Entrada para cálculo de ${calculationType === 'markup' ? 'mark-up' : 'margem'}: Preço=${priceNum}, Custo=${costNum}`);
      
      if (!isNaN(priceNum) && !isNaN(costNum) && costNum > 0 && priceNum > 0) {
        let calculatedMargin: number;

        if (calculationType === 'markup') {
          // Mark-up = ((Preço - Custo) / Custo) * 100
          calculatedMargin = ((priceNum - costNum) / costNum) * 100;
          console.log(`Cálculo de mark-up: ((${priceNum} - ${costNum}) / ${costNum}) * 100 = ${calculatedMargin}%`);
        } else {
          // Margem = ((Preço - Custo) / Preço) * 100
          calculatedMargin = ((priceNum - costNum) / priceNum) * 100;
          console.log(`Cálculo de margem: ((${priceNum} - ${costNum}) / ${priceNum}) * 100 = ${calculatedMargin}%`);
        }
        
        setMargin(formatDecimal(calculatedMargin.toFixed(2)));
        return formatDecimal(calculatedMargin.toFixed(2));
      } else {
        console.log('Valores inválidos para cálculo de margem/markup');
        return '';
      }
    } catch (error) {
      console.error(`Erro ao calcular ${calculationType === 'markup' ? 'mark-up' : 'margem'}:`, error);
      return '';
    }
  };

  const handlePriceChange = (value: string) => {
    console.log(`handlePriceChange chamado com valor: ${value}`);
    // Permite entrada de vírgula
    const formattedValue = value.replace('.', ',');
    setPriceAmount(formattedValue);
    
    // Se tiver custo, calcular margem
    if (costAmount && parseDecimal(costAmount) > 0) {
      updateMarginFromPriceAndCost(formattedValue, costAmount);
    }
  };

  const handleCostChange = (value: string) => {
    console.log(`handleCostChange chamado com valor: ${value}`);
    // Permite entrada de vírgula
    const formattedValue = value.replace('.', ',');
    setCostAmount(formattedValue);
    
    // Se tiver preço, calcular margem
    if (priceAmount && parseDecimal(priceAmount) > 0) {
      updateMarginFromPriceAndCost(priceAmount, formattedValue);
    } 
    // Se preço for zero ou vazio e tiver margem, calcular preço
    else if ((!priceAmount || parseDecimal(priceAmount) === 0) && margin && parseDecimal(margin) > 0) {
      console.log("Calculando preço a partir do custo e margem");
      updatePriceFromCostAndMargin(formattedValue, margin);
    }
  };

  const handleMarginChange = (value: string) => {
    console.log(`handleMarginChange chamado com valor: ${value}`);
    // Permite entrada de vírgula
    const formattedValue = value.replace('.', ',');
    setMargin(formattedValue);
    
    // Se tiver custo, calcular preço
    if (costAmount && parseDecimal(costAmount) > 0) {
      console.log("Calculando preço a partir do custo e margem");
      updatePriceFromCostAndMargin(costAmount, formattedValue);
    }
  };



  const createProductInHubspot = async (planData: any, priceRecords: any[]) => {
    try {
      console.log('🔍 Montando payload para criação no HubSpot:', planData);
      
      // Mapear service_type para hs_product_type
      const getHubSpotProductType = (serviceType: string) => {
        console.log('🔍 Mapeando service_type:', serviceType, 'tipo:', typeof serviceType);
        
        // Normalizar o valor (trim e lowercase)
        const normalizedType = serviceType?.trim()?.toLowerCase();
        console.log('🔍 Valor normalizado:', normalizedType);
        
        if (normalizedType === 'licensing') {
          console.log('✅ Mapeado para license');
          return 'license';
        }
        if (normalizedType === 'material') {
          console.log('✅ Mapeado para material');
          return 'material';
        }
        if (normalizedType === 'program') {
          console.log('✅ Mapeado para program');
          return 'program';
        }
        if (normalizedType === 'professional_service') {
          console.log('✅ Mapeado para service');
          return 'service';
        }
        console.log('✅ Mapeado para service (default)');
        return 'service'; // Default para todos os outros casos
      };

      // Mapear billing_period_type para recurringbillingfrequency
      const getRecurringBillingFrequency = (billingPeriodType: string) => {
        const frequencyMap: Record<string, string> = {
          'month': 'monthly',
          'year': 'annually',
          'quarter': 'quarterly',
          'semiannual': 'semiannually',
          'one-time': 'one-time'
        };
        return frequencyMap[billingPeriodType] || 'monthly';
      };

      // Montar payload conforme especificação
      const payload: any = {
        name: planData.name
      };

      // Adicionar campos opcionais apenas se tiverem valores
      if (planData.sku && planData.sku.trim() !== '') {
        payload.hs_sku = planData.sku;
      }

      // Adicionar campos opcionais apenas se tiverem valores
      if (planData.description && planData.description.trim() !== '') {
        payload.description = planData.description;
      }

      // Buscar preços em BRL e USD
      const brlPrice = priceRecords.find(price => price.currency === 'BRL' && price.price_amount);
      const usdPrice = priceRecords.find(price => price.currency === 'USD' && price.price_amount);

      if (brlPrice && brlPrice.price_amount) {
        payload.hs_price_brl = brlPrice.price_amount.toString();
      }

      if (usdPrice && usdPrice.price_amount) {
        payload.hs_price_usd = usdPrice.price_amount.toString();
      }

      // Adicionar frequência de cobrança apenas se não for one-time
      if (planData.billing_period_type !== 'one-time') {
        payload.recurringbillingfrequency = getRecurringBillingFrequency(planData.billing_period_type);
      }

      // Adicionar tipo de produto
      console.log('🔍 DEBUG service_type antes do mapeamento:', {
        value: planData.service_type,
        type: typeof planData.service_type,
        length: planData.service_type?.length
      });
      payload.hs_product_type = getHubSpotProductType(planData.service_type);

      // Adicionar custo dos produtos vendidos (usar o primeiro custo disponível)
      const firstPriceWithCost = priceRecords.find(price => price.cost_amount);
      if (firstPriceWithCost && firstPriceWithCost.cost_amount) {
        payload.hs_cost_of_goods_sold = firstPriceWithCost.cost_amount.toString();
      }

      console.log('📤 Payload sendo enviado para edge function:', JSON.stringify(payload, null, 2));
      console.log('🌐 URL da edge function:', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-product-hubspot`);

      // Fazer a requisição para a edge function
      const response = await axios.post(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-product-hubspot`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          }
        }
      );

      console.log('📥 Resposta do HubSpot:', response.data);
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || 'Produto criado com sucesso no HubSpot'
      };
    } catch (error: any) {
      console.error('❌ Erro ao criar produto no HubSpot:', error.response?.data || error.message);
      console.error('🔍 Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Erro desconhecido ao criar produto no HubSpot'
      };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFamily) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar plano',
        description: 'Selecione uma família de planos.',
      });
      return;
    }
    
    // Validate at least one price record is valid
    const validPriceRecord = priceRecords.find(record => 
      record.price_amount && parseFloat(record.price_amount) > 0
    );

    if (!validPriceRecord) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar plano',
        description: 'Informe pelo menos um preço válido para o plano.',
      });
      setActiveTab("precos");
      return;
    }

    // Validate unit_measure when pricing_model is 'per unit'
    if (pricingModel === 'per unit' && (!unitMeasure || unitMeasure.trim() === '')) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar plano',
        description: 'Para o modelo de precificação "Por Unidade", é obrigatório definir a unidade de medida.',
      });
      setActiveTab("plano");
      return;
    }

    try {
      // Create subscription plan
      const { data: planData, error: planError } = await supabase
        .from('subscription_plans')
        .insert({
          name,
          description: description || null,
          family_id: selectedFamily,
          sku: sku || null,
          billing_period_type: reverseNormalizeBillingFrequency(billingFrequency),
          billing_frequency: reverseNormalizeBillingFrequency(billingFrequency), // Sincronizar com billing_period_type
          pricing_model: pricingModel,
          unit_measure: pricingModel === 'per unit' ? (unitMeasure || 'user') : null,
          minimum_quantity: parseInt(minimumQuantity),
          maximum_quantity: parseInt(maximumQuantity),
          billing_model: billingModel,
          billing_period_duration: parseInt(billingPeriodDuration),
          subscription_period_type: subscriptionPeriodType,
          subscription_period_duration: parseInt(subscriptionPeriodDuration),
          vendor_id: selectedVendor,
          vendor_subscription_name: vendorSubscriptionName || null,
          vendor_part_number: vendorPartNumber || null,
          service_type: serviceType,
          is_active: true,
          created_by: user?.id,
          updated_by: user?.id,
        })
        .select()
        .single();

      if (planError) throw planError;

      // Create price records in subscription_plan_prices
      const pricePromises = priceRecords
        .filter(record => record.price_amount && parseFloat(record.price_amount) > 0)
        .map(record => supabase
          .from('subscription_plan_prices')
          .insert({
            subscription_plan_id: planData.id,
            currency: record.currency,
            price_amount: parseFloat(record.price_amount),
            cost_amount: record.cost_amount ? parseFloat(record.cost_amount) : null,
            margin: record.margin ? parseFloat(record.margin) : null,
          }));

      // O SKU será gerado automaticamente pelo banco de dados
      const finalPlanData = planData;

      await Promise.all(pricePromises);

      // Encontrar preços em BRL e USD
      const brlPrice = priceRecords.find(price => price.currency === 'BRL' && price.price_amount);
      const usdPrice = priceRecords.find(price => price.currency === 'USD' && price.price_amount);

      console.log('🔍 Dados do plano criado:', {
        id: finalPlanData.id,
        name: finalPlanData.name,
        sku: finalPlanData.sku,
        type: typeof finalPlanData.id
      });

      // Criar produto no HubSpot com o SKU atualizado
      const hubspotResult = await createProductInHubspot(
        finalPlanData, priceRecords
      );

      if (hubspotResult.success && hubspotResult.data && hubspotResult.data.id) {
        console.log(`Salvando ID do HubSpot ${hubspotResult.data.id} para o plano ${planData.id}`);
        
        // Salvar o ID do HubSpot na tabela external_ids
        const { error: hubspotIdError } = await supabase
          .from('external_ids')
          .insert({
            entity_name: 'subscription_plans',
            entity_id: planData.id,
            system_name: 'hubspot',
            external_id: hubspotResult.data.id
          });

        if (hubspotIdError) {
          console.error('Erro ao salvar ID do HubSpot:', hubspotIdError);
                  toast({
          variant: 'default',
          title: 'Aviso',
          description: 'Plano criado com sucesso, mas houve um erro ao salvar o ID do HubSpot.',
        });
        }
      } else if (!hubspotResult.success) {
        toast({
          variant: 'default',
          title: 'Aviso',
          description: `Plano criado com sucesso, mas houve um erro ao criar no HubSpot: ${hubspotResult.message}`,
        });
      }

      toast({
        title: 'Plano criado',
        description: 'O plano foi criado com sucesso.',
      });

      resetForm();
      navigate('/subscription-plans');
    } catch (error) {
      console.error('Error creating plan:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao criar plano',
        description: 'Não foi possível criar o plano. Por favor, tente novamente.',
      });
    }
  };

  if (loading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex justify-center items-center h-[60vh]">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 pl-3 lg:pl-4">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={() => navigate('/subscription-plans')}
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Voltar
                </Button>
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <PlusCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Novo Plano de Assinatura</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Configure um novo plano para seus clientes
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={handleSubmit}
                size="default"
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              >
                <Save className="h-4 w-4 mr-2" />
                Salvar Plano
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 -mt-4 relative z-10">

        {/* Enhanced Main Content with Modern Design */}
        <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm mb-8">
          <CardContent className="p-6">
            {/* Navigation Tabs */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('plano')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'plano'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Plano
                </button>
                <button
                  onClick={() => setActiveTab('precos')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'precos'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Preços
                </button>
                <button
                  onClick={() => setActiveTab('cobranca')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'cobranca'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Cobrança
                </button>
                <button
                  onClick={() => setActiveTab('fornecedor')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'fornecedor'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Fornecedor
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Seção de Plano */}
              {activeTab === 'plano' && (
                <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-base flex items-center gap-2">
                        Nome <Badge variant="outline" className="ml-2 text-xs">Obrigatório</Badge>
                      </Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="text-base"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-base">Descrição</Label>
                      <Textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="text-base min-h-[120px]"
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="family" className="text-base flex items-center gap-2">
                        Família de Planos <Badge variant="outline" className="ml-2 text-xs">Obrigatório</Badge>
                      </Label>
                      <Select value={selectedFamily} onValueChange={setSelectedFamily} required>
                        <SelectTrigger className="text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {planFamilies.map((family) => (
                            <SelectItem key={family.id} value={family.id}>
                              {family.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sku" className="text-base">SKU (Opcional)</Label>
                      <Input
                        id="sku"
                        value={sku}
                        onChange={(e) => setSku(e.target.value)}
                        className="text-base"
                        placeholder="Deixe em branco para gerar automaticamente"
                      />
                      <p className="text-xs text-muted-foreground">
                        Se não preenchido, um SKU único no formato "528319-824" será gerado automaticamente pelo sistema
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service_type" className="text-base flex items-center gap-2">
                        Tipo de Serviço <Badge variant="outline" className="ml-2 text-xs">Obrigatório</Badge>
                      </Label>
                      <Select defaultValue="licensing" onValueChange={setServiceType}>
                        <SelectTrigger id="service_type" className="text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="licensing">Licenciamento</SelectItem>
                          <SelectItem value="professional_service">Serviço Profissional</SelectItem>
                          <SelectItem value="material">Material</SelectItem>
                          <SelectItem value="program">Programa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                </div>
              )}
              
              {/* Seção de Preços */}
              {activeTab === 'precos' && (
                <div className="space-y-8">
                    <div className="flex justify-end">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="calculation-type"
                          checked={calculationType === 'margin'}
                          onCheckedChange={(checked) => setCalculationType(checked ? 'margin' : 'markup')}
                        />
                        <Label htmlFor="calculation-type" className="text-sm font-extrabold">
                          Calcular por {calculationType === 'markup' ? 'Mark-up' : 'Margem'}
                        </Label>
                        <span className="text-xs text-muted-foreground ml-2">
                          {calculationType === 'markup' 
                            ? "Mark-up: Preço = Custo × (1 + Mark-up/100)" 
                            : "Margem: Preço = Custo / (1 - Margem/100)"}
                        </span>
                      </div>
                    </div>

                    {priceRecords.length > 0 ? (
                      <>
                        <div className="rounded-md border overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Moeda</TableHead>
                                <TableHead>Preço</TableHead>
                                <TableHead>Custo</TableHead>
                                <TableHead>Margem (%)</TableHead>
                                <TableHead className="w-[100px] text-right">Ações</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {priceRecords.map((price) => (
                                <TableRow key={price.id}>
                                  <TableCell>{price.currency}</TableCell>
                                  <TableCell>{price.price_amount ? price.price_amount : "-"}</TableCell>
                                  <TableCell>{price.cost_amount ? price.cost_amount : "-"}</TableCell>
                                  <TableCell>{price.margin ? price.margin : "-"}</TableCell>
                                  <TableCell className="text-right">
                                    <Button 
                                      variant="ghost" 
                                      size="icon"
                                      onClick={() => removePriceRecord(price.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    ) : (
                      <div className="text-center mb-4">
                        <p>Nenhum preço configurado para este plano.</p>
                      </div>
                    )}

                    <div className="bg-muted/40 p-4 rounded-lg space-y-4">
                      <h3 className="font-extrabold">Adicionar Preço</h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="currency" className="text-sm">Moeda</Label>
                          <Select value={currency} onValueChange={setCurrency}>
                            <SelectTrigger className="text-base">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BRL">Real (BRL)</SelectItem>
                              <SelectItem value="USD">Dólar (USD)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="cost_amount" className="text-sm">Custo</Label>
                          <Input
                            id="cost_amount"
                            type="text"
                            inputMode="decimal"
                            value={costAmount}
                            onChange={(e) => handleCostChange(e.target.value)}
                            className="text-base"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="price_amount" className="text-sm">Preço</Label>
                          <Input
                            id="price_amount"
                            type="text"
                            inputMode="decimal"
                            value={priceAmount}
                            onChange={(e) => handlePriceChange(e.target.value)}
                            className="text-base"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="margin" className="text-sm">
                            {calculationType === 'markup' ? 'Mark-up' : 'Margem'} (%)
                          </Label>
                          <Input
                            id="margin"
                            type="text"
                            inputMode="decimal"
                            value={margin}
                            onChange={(e) => handleMarginChange(e.target.value)}
                            className="text-base"
                          />
                        </div>

                        <div className="flex justify-end space-x-2 mt-4 col-span-4">
                          <Button type="button" onClick={addPriceRecord}>
                            Adicionar Preço
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <Label htmlFor="pricing_model" className="text-sm flex items-center gap-2">
                        Modelo de Precificação <Badge variant="outline" className="ml-2 text-xs">Obrigatório</Badge>
                      </Label>
                      <Select value={pricingModel} onValueChange={setPricingModel}>
                        <SelectTrigger className="text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">Valor Fixo</SelectItem>
                          <SelectItem value="per unit">Por Unidade</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {pricingModel === 'per unit' && (
                      <div className="bg-muted/40 p-4 rounded-lg space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="unit_measure" className="text-sm">Unidade de Medida</Label>
                            <Select value={unitMeasure || 'user'} onValueChange={setUnitMeasure}>
                              <SelectTrigger id="unit_measure">
                                <SelectValue placeholder="Selecione a unidade de medida" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">Por usuário</SelectItem>
                                <SelectItem value="hour">Por hora</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="minimum_quantity" className="text-sm flex items-center gap-2">
                              Quantidade Mínima <Badge variant="outline" className="ml-2 text-[10px]">Padrão: 1</Badge>
                            </Label>
                            <Input
                              id="minimum_quantity"
                              type="number"
                              min="0"
                              value={minimumQuantity}
                              onChange={(e) => setMinimumQuantity(e.target.value)}
                              className="text-base"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="maximum_quantity" className="text-sm flex items-center gap-2">
                              Quantidade Máxima <Badge variant="outline" className="ml-2 text-[10px]">Padrão: 100</Badge>
                            </Label>
                            <Input
                              id="maximum_quantity"
                              type="number"
                              min="0"
                              value={maximumQuantity}
                              onChange={(e) => setMaximumQuantity(e.target.value)}
                              className="text-base"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              )}
              
              {/* Seção de Cobrança */}
              {activeTab === 'cobranca' && (
                <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="billing_model" className="text-base flex items-center gap-2">
                        Modelo de Cobrança <Badge variant="outline" className="ml-2 text-xs">Padrão: Na criação</Badge>
                      </Label>
                      <Select value={billingModel} onValueChange={setBillingModel}>
                        <SelectTrigger className="text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="on creation">Na criação</SelectItem>
                          <SelectItem value="on activation">Na ativação</SelectItem>
                          <SelectItem value="on usage">No uso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="billing_frequency" className="text-base flex items-center gap-2">
                          Período de Cobrança <Badge variant="outline" className="ml-2 text-xs">Padrão: Mensal</Badge>
                        </Label>
                        <Select value={billingFrequency} onValueChange={setBillingFrequency}>
                          <SelectTrigger className="text-base">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="month">Mensal</SelectItem>
                            <SelectItem value="year">Anual</SelectItem>
                            <SelectItem value="one-time">Pagamento Único</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="billing_period_duration" className="text-base">
                          Duração do Período de Cobrança
                        </Label>
                        <Input
                          id="billing_period_duration"
                          type="number"
                          min="1"
                          value={billingPeriodDuration}
                          onChange={(e) => setBillingPeriodDuration(e.target.value)}
                          placeholder={`Número de ${billingFrequency === 'month' ? 'meses' : billingFrequency === 'year' ? 'anos' : 'pagamentos'}`}
                          className="text-base"
                        />
                      </div>
                    </div>

                    <Separator />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="subscription_period_type" className="text-base flex items-center gap-2">
                          Período de Assinatura <Badge variant="outline" className="ml-2 text-xs">Padrão: Mês</Badge>
                        </Label>
                        <Select value={subscriptionPeriodType} onValueChange={setSubscriptionPeriodType}>
                          <SelectTrigger className="text-base">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="month">Mensal</SelectItem>
                            <SelectItem value="year">Anual</SelectItem>
                            <SelectItem value="one-time">Pagamento Único</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subscription_period_duration" className="text-base">
                          Duração do Período de Assinatura
                        </Label>
                        <Input
                          id="subscription_period_duration"
                          type="number"
                          min="1"
                          value={subscriptionPeriodDuration}
                          onChange={(e) => setSubscriptionPeriodDuration(e.target.value)}
                          placeholder={`Número de ${subscriptionPeriodType === 'month' ? 'meses' : subscriptionPeriodType === 'year' ? 'anos' : 'pagamentos'}`}
                          className="text-base"
                          disabled={subscriptionPeriodType === 'one-time'}
                        />
                      </div>
                    </div>

                </div>
              )}
              
              {/* Seção de Fornecedor */}
              {activeTab === 'fornecedor' && (
                <div className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="vendor" className="text-base">Fornecedor</Label>
                      <Select value={selectedVendor} onValueChange={setSelectedVendor}>
                        <SelectTrigger className="text-base">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nenhum</SelectItem>
                          {vendors.map((vendor) => (
                            <SelectItem key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedVendor !== 'none' && (
                      <div className="bg-muted/40 p-4 rounded-lg space-y-6">
                        <div className="space-y-2">
                          <Label htmlFor="vendor_subscription_name" className="text-base">
                            Nome da Assinatura no Fornecedor
                          </Label>
                          <Input
                            id="vendor_subscription_name"
                            value={vendorSubscriptionName}
                            onChange={(e) => setVendorSubscriptionName(e.target.value)}
                            className="text-base"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="vendor_part_number" className="text-base">
                            Número da Parte no Fornecedor
                          </Label>
                          <Input
                            id="vendor_part_number"
                            value={vendorPartNumber}
                            onChange={(e) => setVendorPartNumber(e.target.value)}
                            className="text-base"
                          />
                        </div>
                      </div>
                    )}
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Bottom Spacing */}
        <div className="h-16" />
      </div>
    </div>
  );
} 